import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { InboxMessage, InboxSendRequest, InboxThread } from "../../src/inbox-v2/contract.ts";
import type {
  DraftSendReference,
  InboxDraftSendRequest,
} from "../../src/inbox-v2/draft-send-contract.ts";
import type { ForwardRecipients } from "../../src/inbox-v2/forward-contract.ts";
import type { ReplySender, ReplySignature } from "../../src/mailbox-reply-signature-contract.ts";
import {
  type ComposerReply,
  LEAD_REPLY,
  type PersonalComposer,
} from "../../src/personal-composer-contract.ts";
import type { SignOffMode } from "../../src/sign-off-mode.ts";
import { OPEN_COMPOSE_EVENT } from "../compose.tsx";
import { LinkButton, LinkLayer, useEditorLinks } from "../editor-links.tsx";
import { BoldIcon, CheckIcon, ItalicIcon, ListIcon, ReplyIcon, SendIcon } from "../icons.tsx";
import { fieldCapability } from "./compose-capabilities.ts";
import {
  type ComposeMode,
  ComposerFoot,
  ComposerHead,
  FieldRow,
  FormatSwitch,
} from "./composer.tsx";
import { boxReply, savedRecipientText } from "./composer-reply.ts";
import {
  BoxHints,
  type DraftAids,
  EndingButton,
  type EndingChoice,
  EndingChoices,
  useEndingChoice,
  WaitingLine,
} from "./draft-aids.tsx";
import { ForwardBox } from "./forward-box.tsx";
import type { ReplyHold } from "./held-sends.tsx";
import { personName } from "./list.tsx";
import { openForward, sourceWords } from "./message-actions.tsx";
import { useOutgoingRecorder } from "./outgoing-reply.ts";
import type { ComposerLocalBase, ComposerWords } from "./personal-composer-content.ts";
import {
  capturedReplyMatches,
  useComposerHydration,
  useKeptReply,
  useReplyLineage,
} from "./personal-composer-editor.ts";
import { AttachButton, ReplyFileList, useFileDrop } from "./reply-attachments.tsx";
import {
  type BoxContent,
  type BoxFiles,
  holdsOtherThan,
  paragraphs,
  REPLY_EXTENSIONS,
  type ReplyBoxLink,
  useBoxApproving,
  type WholeDraft,
} from "./reply-box-link.ts";
import { hasOthers, openReply } from "./reply-message.tsx";
import {
  heldAudience,
  type ReplyRecipientEdit,
  ReplyRecipientRows,
  ReplyRecipientsNotice,
  replyAudience,
  useReplyRecipients,
} from "./reply-recipient-fields.tsx";
import { SignatureTool, senderFirstName, useReplySender } from "./reply-sender.tsx";
import { endsWithSignature, ownWords, signatureStart, withSignature } from "./reply-signature.ts";
import { claimedWords, HoldRow, Uncertain } from "./send-states.tsx";
import {
  type BoxKey,
  keyOf,
  type MessageBox,
  TOP,
  type TopSource,
  useBoxes,
} from "./thread-boxes.tsx";
import { adoptUnsent, keepUnsent, readUnsent, replaceUnsent, unsentKey } from "./unsent-reply.ts";
import { useDraftSend } from "./use-draft-send.ts";
import {
  type PersonalComposerControl,
  PersonalComposerNotice,
  usePersonalComposer,
} from "./use-personal-composer.tsx";
import { type ReplyFiles, useReplyFiles } from "./use-reply-files.ts";
import { useReplySend } from "./use-reply-send.ts";

type Format = "text" | "html";
type Sending = ReturnType<typeof useReplySend>;

/** The reply agent's draft as a starting point (`agent/agent-item.tsx`): the whole email, both forms. */
export type StartingDraft = {
  readonly revision: number;
  readonly html: string;
  readonly plainText: string;
  /** Shown only while the box holds this draft: its hints, its ending, what it waits on. */
  readonly aids?: DraftAids;
  /** Loading waits while a replacement is requested or the conversation is read again. */
  readonly held?: boolean;
  /** Questions may still be answered while the rep edits; approval waits for those answers. */
  readonly sendBlocked?: boolean;
  readonly prepareSend?: () => Promise<DraftSendReference>;
};

const REFUSALS: Record<string, string> = {
  already_answered: "this message was already answered in Bison",
  conversation_changed: "the conversation changed while your reply was held",
  sending_not_available: "sending was switched off for this conversation while your reply was held",
  workspace_moved: "sending for this workspace moved while your reply was held",
  files_unreadable: "its files could not be read before sending",
};

function refusalText(reason: string | null | undefined): string {
  return (
    (reason ? REFUSALS[reason] : undefined) ?? "your reply could not be checked before sending"
  );
}

const firstName = (thread: InboxThread) =>
  personName(thread.conversation).split(/\s+/u)[0] ?? "the lead";

/** The editor while it can be written to; null before it exists and after it is gone. */
const liveEditor = (editor: Editor | null): Editor | null =>
  editor && !editor.isDestroyed ? editor : null;

/** An empty Formatted box holding only the signature, a paragraph above it for the words. */
function fillEditor(editor: Editor, html: string): { text: string; html: string } {
  editor.commands.setContent(`<p></p>${html}`, { emitUpdate: false });
  return { text: editor.getText({ blockSeparator: "\n\n" }), html: editor.getHTML() };
}

/** The signature added after the editor's words, or taken off their end where they end with it. */
function editEditorSignature(
  editor: Editor,
  change: "add" | "remove",
  signature: ReplySignature,
): void {
  const { doc } = editor.state;
  if (change === "add") {
    editor.chain().focus().insertContentAt(doc.content.size, signature.html).run();
    return;
  }
  const blocks: { from: number }[] = [];
  doc.forEach((_node, offset) => {
    blocks.push({ from: offset });
  });
  const start = signatureStart(
    blocks,
    doc.content.size,
    (from, to) => doc.textBetween(from, to, "\n", "\n"),
    signature.plainText,
  );
  if (start !== null)
    editor.chain().focus().deleteRange({ from: start, to: doc.content.size }).run();
}

/**
 * The text being written, in either format, and where it is kept. Every change is written through
 * at once, so leaving the conversation never depends on a timer having fired.
 */
function useReplyText(
  key: string,
  thread: InboxThread,
  frozen: boolean,
  signature: ReplySignature | null,
  onEdit: (words: ComposerWords) => ComposerLocalBase | undefined,
) {
  // The lead may write again while the box is open; it stays open, and the text says so.
  const messageId = thread.conversation.leadMessageId;
  const stored = useMemo(() => readUnsent(key), [key]);
  const signatureRef = useRef(signature);
  signatureRef.current = signature;
  // Whether this box has had its chance at the signature: kept words had theirs before.
  const signed = useRef(stored !== null);
  const [format, setFormat] = useState<Format>(stored?.format ?? thread.replyFormat ?? "text");
  const [plain, setPlain] = useState(stored?.format === "text" ? stored.text : "");
  const [rich, setRich] = useState(stored?.format === "html" ? stored.text : "");
  const [warned, setWarned] = useState(false);
  // The editor's markup, so a change of formatting alone is seen by the agent's card too.
  const [richHtml, setRichHtml] = useState(stored?.format === "html" ? stored.html : "");
  // The draft revision "Edit in reply box" put here; a ref too, for the editor's own callback.
  const { fromDraft, fromDraftRef, setFromDraft, resetFromDraft } = useReplyLineage(
    stored?.fromDraft ?? null,
  );
  // Each whole draft put in counts: a load is no editor update, so what reads the editor reads again.
  const [loads, setLoads] = useState(0);
  const { writtenTo, setWrittenTo, kept, setKept, keep } = useKeptReply({
    key,
    messageId,
    stored,
    signature: signatureRef,
    fromDraft: fromDraftRef,
    onEdit,
  });
  const editor = useEditor({
    extensions: REPLY_EXTENSIONS,
    content: stored?.format === "html" ? stored.html : "",
    onUpdate: ({ editor: current, transaction }) => {
      const text = current.getText({ blockSeparator: "\n\n" });
      let replaced = false;
      transaction.steps.forEach((step, index) => {
        const doc = transaction.docs[index];
        step.getMap().forEach((from, to) => {
          // Both document replacement and replacement of all editable text (Ctrl/Cmd+A).
          if (doc && from <= 1 && to >= doc.content.size - 1 && to > from) replaced = true;
        });
      });
      if (!text.trim() || replaced) setFromDraft(null);
      setRich(text);
      setRichHtml(current.getHTML());
      keep("html", text, current.getHTML());
    },
    editorProps: { attributes: { "aria-label": "Your reply" } },
  });
  const hydrate = useComposerHydration({
    key,
    editor,
    format,
    signed,
    setFormat,
    setPlain,
    setRich,
    setRichHtml,
    setFromDraft,
    setWrittenTo,
    setLoads,
    setKept,
  });
  useEffect(() => {
    // Freezing is not an edit: without `false` the editor reports an update, and an empty one
    // would forget the kept text.
    editor?.setEditable(!frozen, false);
  }, [editor, frozen]);
  const text = format === "html" ? rich : plain;
  // A box that opens empty already holds the signature, once: a rep who deletes it is not overruled.
  useEffect(() => {
    if (signed.current || signature === null) return;
    const live = liveEditor(editor);
    if (format === "html" && !live) return;
    signed.current = true;
    if (text.trim() !== "" || fromDraft !== null) return;
    if (format === "text" || !live) {
      setPlain(withSignature("", signature.plainText));
      return;
    }
    const filled = fillEditor(live, signature.html);
    setRich(filled.text);
    setRichHtml(filled.html);
  }, [signature, editor, format, text, fromDraft]);
  // A sent reply is done: the box empties, so a later message from the lead starts a fresh one.
  const clear = useCallback(() => {
    setPlain("");
    setRich("");
    setRichHtml("");
    fromDraftRef.current = null;
    resetFromDraft(null);
    editor?.commands.clearContent(false);
    // Emptying the words runs the signature's effect again, now allowed to fill the box.
    signed.current = false;
  }, [editor, fromDraftRef, resetFromDraft]);
  const mine = ownWords(text, signature?.plainText ?? null);
  const signatureEdit = (change: "add" | "remove") => {
    const current = signatureRef.current;
    if (frozen || current === null) return;
    if (format === "text") {
      const next =
        change === "add"
          ? withSignature(plain, current.plainText)
          : ownWords(plain, current.plainText);
      setPlain(next);
      keep("text", next, "");
      return;
    }
    const live = liveEditor(editor);
    if (live) editEditorSignature(live, change, current);
  };
  /** Whether the box still holds exactly what a pending save captured. */
  const holds = (snapshot: BoxContent) =>
    capturedReplyMatches(
      {
        format,
        plainText: text,
        html: editor?.getHTML() ?? "",
        fromDraft: fromDraftRef.current,
      },
      snapshot,
    );
  /** A draft revision put in whole, as the format shown takes it: markup, or its plain words. */
  const load = (html: string, words: string, revision: number) => {
    setFromDraft(revision);
    setLoads((count) => count + 1);
    if (format === "text") {
      setPlain(words);
      keep("text", words, "");
      return;
    }
    const live = editor?.isDestroyed ? null : editor;
    live?.commands.setContent(html, { emitUpdate: false });
    const read = live?.getText({ blockSeparator: "\n\n" }) ?? words;
    setRich(read);
    setRichHtml(live?.getHTML() ?? html);
    keep("html", read, live?.getHTML() ?? html);
  };
  return {
    editor,
    format,
    text,
    clear,
    kept,
    warned,
    /** Nothing of the rep's: empty, or only the signature the box opened with. */
    empty: mine === "",
    /** The words end with the mailbox's signature. */
    signed: endsWithSignature(text, signature?.plainText ?? null),
    /** Written to an earlier lead message than the newest, and not edited since: worth a look. */
    stale: mine !== "" && writtenTo !== messageId,
    html: () => (format === "html" ? (editor?.getHTML() ?? "") : ""),
    richHtml,
    fromDraft,
    loads,
    /** The box's words are now this draft revision, as the card's Approve saved them. */
    adopt: (snapshot: BoxContent, revision: number) => {
      if (!holds(snapshot)) return;
      setFromDraft(revision);
      keep(format, format === "html" ? rich : plain, format === "html" ? richHtml : "");
    },
    load,
    hydrate,
    /** The box's words became this whole revision ("Change ending"), if the box still holds them. */
    replace: (snapshot: BoxContent, draft: WholeDraft) => {
      if (holds(snapshot)) load(draft.html, draft.plainText, draft.revision);
    },
    write: (value: string, replaced = false) => {
      if (frozen) return;
      if (!value.trim() || replaced) setFromDraft(null);
      setPlain(value);
      keep("text", value, "");
    },
    /** The signature after the words, a blank line between, wherever the caret is. */
    addSignature: () => signatureEdit("add"),
    /** The signature the words end with, taken off; anything after it would have kept it. */
    removeSignature: () => signatureEdit("remove"),
    choose: (next: Format) => {
      if (next === format) return;
      if (next === "text") {
        setWarned(/<(strong|em|ul|ol|a)\b/u.test(editor?.getHTML() ?? ""));
        setPlain(rich);
        keep("text", rich, "");
      } else {
        setWarned(false);
        editor?.commands.setContent(paragraphs(plain), { emitUpdate: false });
        setRich(plain);
        setRichHtml(editor?.getHTML() ?? "");
        keep("html", plain, editor?.getHTML() ?? "");
      }
      setFormat(next);
    },
  };
}
type ReplyText = ReturnType<typeof useReplyText>;

/** "Edit in reply box", while a draft is on offer and the box takes edits. */
type StartFromDraft = {
  /** Loads the draft into the box; asks first where the box holds words of the rep's own. */
  start: () => void;
  asking: boolean;
  replace: () => void;
  keep: () => void;
  /** This draft is already in the box, including any edits the rep made to it. */
  loaded: boolean;
  /** A "Draft again" is being asked: drawn, but it loads nothing until the request is answered. */
  held: boolean;
  /** What the draft would replace: the rep's own words, and how many files they attached. */
  words: boolean;
  files: number;
};

/**
 * The question before the draft replaces the box, naming what goes: the rep's words, the files they
 * attached (the draft's own, if any, take their place), or both.
 */
function replaceQuestion(words: boolean, files: number): string {
  const off =
    files === 1
      ? "The file you attached comes off the reply."
      : `The ${files} files you attached come off the reply.`;
  if (files === 0) return "Replace what you wrote with the draft?";
  return words ? `Replace what you wrote with the draft? ${off}` : `Load the draft? ${off}`;
}

/** The box as the draft's offer compares it: a box holding only the signature holds nothing. */
const boxWords = (reply: ReplyText) => ({
  format: reply.format,
  text: reply.empty ? "" : reply.text,
  html: reply.richHtml,
});

/**
 * Opens the box on the draft: at once into an empty box, the caret at its end; over words the rep
 * wrote, only after they choose Replace. `reveal` opens the box, with the caret or without it.
 */
function useStartFromDraft(
  reply: ReplyText,
  draft: StartingDraft | null,
  frozen: boolean,
  reveal: (caret: boolean) => void,
  /** The box's files: the draft's replace them, so a box holding files asks first too. */
  files: { readonly count: number; readonly load: (revision: number) => void },
): StartFromDraft | null {
  const [asking, setAsking] = useState(false);
  if (!draft || frozen) return null;
  const held = draft.held === true;
  const load = () => {
    if (held) return;
    reply.load(draft.html, draft.plainText, draft.revision);
    files.load(draft.revision);
    setAsking(false);
    reveal(true);
  };
  return {
    asking,
    start: () => {
      if (held) return;
      // An empty box, or one holding the draft as it stands, has nothing of the rep's to lose.
      const box = boxWords(reply);
      if (!holdsOtherThan(box, draft) && files.count === 0) return load();
      setAsking(true);
      reveal(false);
    },
    replace: load,
    keep: () => setAsking(false),
    held,
    words: holdsOtherThan(boxWords(reply), draft),
    files: files.count,
    loaded: reply.fromDraft === draft.revision && reply.text.trim() !== "",
  };
}

function DraftEntry({ start }: { start: StartFromDraft | null }) {
  if (!start || start.loaded) return null;
  return (
    <button
      type="button"
      className="lcr-i2-start-draft lcr-linklike"
      disabled={start.held || start.asking}
      onClick={start.start}
    >
      Use draft
    </button>
  );
}

/** Asked before the draft replaces what the rep wrote; Escape keeps theirs. */
function ReplaceConfirm(props: { start: StartFromDraft }) {
  const { start } = props;
  const box = useRef<HTMLFieldSetElement>(null);
  useEffect(() => box.current?.focus(), []);
  return (
    <fieldset
      ref={box}
      className="lcr-reply-confirm lcr-i2-replace"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        event.stopPropagation();
        start.keep();
      }}
    >
      <legend>{replaceQuestion(start.words, start.files)}</legend>
      <button
        type="button"
        className="lcr-small-button"
        disabled={start.held}
        onClick={start.replace}
      >
        Replace
      </button>
      <button type="button" className="lcr-small-button lcr-ghost" onClick={start.keep}>
        Keep mine
      </button>
    </fieldset>
  );
}

/** What the box holds now, its markup read fresh for draft saves and the agent's state line. */
const boxContent = (now: ReplyText, open: boolean): BoxContent => {
  const text = now.empty ? "" : now.text;
  return {
    format: now.format,
    text,
    html: now.empty ? paragraphs("") : now.format === "html" ? now.html() : paragraphs(text),
    open,
    loaded: now.fromDraft,
  };
};

function useLinked(
  link: ReplyBoxLink | null,
  reply: ReplyText,
  start: StartFromDraft | null,
  open: boolean,
  planId: string,
  key: string,
  files: ReplyFiles,
) {
  const boxFiles = useLinkedFiles(files);
  const latest = useRef({ reply, start, open, boxFiles });
  latest.current = { reply, start, open, boxFiles };
  useEffect(() => {
    let connected = true;
    const disconnect = link?.connect({
      planId,
      read: () => ({
        ...boxContent(latest.current.reply, latest.current.open),
        ...(latest.current.boxFiles ? { files: latest.current.boxFiles } : {}),
      }),
      edit: () => latest.current.start?.start(),
      adopt: (snapshot, revision) => {
        adoptUnsent(key, snapshot, revision);
        if (connected) latest.current.reply.adopt(snapshot, revision);
      },
      replace: (snapshot, draft) => {
        replaceUnsent(key, snapshot, draft);
        if (connected) latest.current.reply.replace(snapshot, draft);
      },
    });
    return () => {
      connected = false;
      disconnect?.();
    };
  }, [link, planId, key]);
  const { format, richHtml, fromDraft } = reply;
  const text = reply.empty ? "" : reply.text;
  useEffect(() => {
    link?.publish({
      format,
      text,
      html: text === "" ? paragraphs("") : format === "html" ? richHtml : paragraphs(text),
      open,
      loaded: fromDraft,
      ...(boxFiles ? { files: boxFiles } : {}),
    });
  }, [link, format, text, richHtml, open, fromDraft, boxFiles]);
  useEffect(
    () => () => link?.publish({ format: "text", text: "", html: "", open: false, loaded: null }),
    [link],
  );
}

/**
 * The box's files as the card sees them, when there are any, the draft's were taken off, or one is
 * still going up or did not (so Approve waits for it); the same object while nothing changes, so
 * publishing them redraws nothing.
 */
function useLinkedFiles(files: ReplyFiles): BoxFiles | undefined {
  const reference = files.reference();
  const held = reference !== undefined;
  const ids = reference?.fileIds.join("\n") ?? "";
  const revision = reference?.revision ?? 0;
  const { composerId, edited, ready } = files;
  return useMemo<BoxFiles | undefined>(
    () =>
      held || edited || !ready
        ? { composerId, revision, fileIds: ids ? ids.split("\n") : [], edited, ready }
        : undefined,
    [held, ids, revision, composerId, edited, ready],
  );
}

/** What starting from the draft finds in the box's files, and how it loads the draft's own. */
const draftFiles = (files: ReplyFiles) => ({
  count: files.files.length + files.pending.length,
  load: (revision: number) => void files.adopt(revision),
});

/** The reply agent's cards ask for the box ("Write the reply yourself"): it opens here. */
function useOpenOnRequest(open: () => void, top: boolean) {
  const latest = useRef(open);
  latest.current = open;
  useEffect(() => {
    // The agent's "Write a reply" opens the reply to the lead, never a message's own box.
    if (!top) return;
    const listen = () => latest.current();
    window.addEventListener(OPEN_COMPOSE_EVENT, listen);
    return () => window.removeEventListener(OPEN_COMPOSE_EVENT, listen);
  }, [top]);
}

/** The reply agent's draft, while the box holds it: its aids and "Change ending" (`draft-aids.tsx`). */
type HeldDraft = {
  readonly aids: DraftAids;
  readonly choice: EndingChoice;
  /** Another saved ending can replace this one: the box still ends with the stored one. */
  readonly endable: boolean;
  readonly onChoose: (mode: SignOffMode) => void;
};

/**
 * Whether the box holds the agent's draft, edited or not, rather than the rep's own words; and the
 * aids it then shows. "Change ending" puts the saved revision in whole once the server answers.
 */
function heldDraft(
  reply: ReplyText,
  draft: StartingDraft | null,
  open: boolean,
  choice: EndingChoice,
): HeldDraft | null {
  const aids = draft?.aids ?? null;
  if (aids === null || reply.fromDraft !== draft?.revision) return null;
  return {
    aids,
    choice,
    endable: aids.ending !== null && aids.endingIntact(boxContent(reply, open)),
    onChoose: (mode) => void choice.choose(mode),
  };
}

/** "Change ending" while the box holds the agent's draft: a row of its own, drawn only then. */
function EndingToolbar(props: { frozen: boolean; held: HeldDraft | null; endingId: string }) {
  const { held } = props;
  if (!held?.endable) return null;
  return (
    <div className="lcr-reply-toolbar">
      <EndingButton choice={held.choice} disabled={props.frozen} controls={props.endingId} />
    </div>
  );
}

/** Bold, italics, a list and a link, beside the format switch in the foot while Formatted. */
function FormatTools(props: {
  reply: ReplyText;
  frozen: boolean;
  links: ReturnType<typeof useEditorLinks>;
}) {
  const { reply, frozen } = props;
  const editor = reply.editor;
  if (reply.format !== "html") return null;
  const tool = (label: string, icon: React.ReactNode, active: boolean, run: () => void) => (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={frozen}
      onClick={() => run()}
    >
      {icon}
    </button>
  );
  return (
    <div
      className="lcr-format-toolbar lcr-i2-format-tools"
      role="toolbar"
      aria-label="Email formatting"
    >
      {tool("Bold", <BoldIcon size={16} />, !!editor?.isActive("bold"), () =>
        editor?.chain().focus().toggleBold().run(),
      )}
      {tool("Italic", <ItalicIcon size={16} />, !!editor?.isActive("italic"), () =>
        editor?.chain().focus().toggleItalic().run(),
      )}
      {tool("Bulleted list", <ListIcon size={16} />, !!editor?.isActive("bulletList"), () =>
        editor?.chain().focus().toggleBulletList().run(),
      )}
      <LinkButton links={props.links} disabled={frozen} />
    </div>
  );
}

/** The saved endings once "Change ending" is open. */
function HeldEnding(props: { held: HeldDraft; frozen: boolean; id: string }) {
  const { held } = props;
  if (!held.choice.open || !held.endable || !held.aids.ending) return null;
  return (
    <EndingChoices
      id={props.id}
      ending={held.aids.ending}
      frozen={props.frozen}
      onChoose={held.onChoose}
    />
  );
}

/** Which preset the box is in, and which others its message offers (`composer.tsx`). */
type ReplyMode = {
  readonly mode: ComposeMode;
  readonly word: string | undefined;
  readonly modes: readonly ComposeMode[];
  readonly onMode: (mode: ComposeMode) => void;
  readonly source: string | null;
};

/** The reply's fields: who it goes to, from whom, and its subject, as the route allows them. */
/** "Vovik Tsybulskyi <vovik@…>", as a mail client names the sender; the address alone without a name. */
function fromWords(mailbox: string | null, sender: ReplySender | null): string {
  if (mailbox === null) return "Not on file: Bison sends from the thread's mailbox";
  const name = sender?.name?.trim();
  return name ? `${name} <${mailbox}>` : mailbox;
}

function ReplyFields(props: {
  thread: InboxThread;
  parent: InboxMessage | undefined;
  edit: ReplyRecipientEdit;
  frozen: boolean;
  sender: ReplySender | null;
}) {
  return (
    <>
      <div className="lcr-i2-fields">
        <ReplyRecipientRows edit={props.edit} frozen={props.frozen} />
        <FieldRow
          label="From"
          capability={fieldCapability("reply", "from")}
          value={fromWords(props.thread.mailbox, props.sender)}
        />
        <FieldRow
          label="Subject"
          capability={fieldCapability("reply", "subject")}
          value={props.parent?.subject ?? props.thread.conversation.subject}
        />
      </div>
      <ReplyRecipientsNotice edit={props.edit} />
    </>
  );
}

/** The reply's head: its mode as a menu, whose message it answers, and the fold where there is one. */
function ReplyHead(props: { mode: ReplyMode; onFold: (() => void) | null }) {
  const { mode } = props;
  return (
    <ComposerHead
      mode={mode.mode}
      {...(mode.word ? { word: mode.word } : {})}
      modes={mode.modes}
      onMode={mode.onMode}
      source={mode.source}
      onFold={props.onFold}
    />
  );
}

/**
 * The hints under the words: the agent's draft's, and the rep's own words' once there are any, read
 * against the sending mailbox so an unsigned reply or another person's name is pointed out.
 */
function ReplyHints(props: {
  reply: ReplyText;
  held: HeldDraft | null;
  sender: ReplySender | null;
  links: ReturnType<typeof useEditorLinks>;
  id: string;
  frozen: boolean;
}) {
  const { reply, held, sender } = props;
  if (!held && reply.empty) return null;
  const signature = sender?.signature ?? null;
  return (
    <BoxHints
      key={reply.loads}
      format={reply.format}
      editor={reply.editor}
      text={reply.text}
      ending={held?.aids.ending ?? null}
      signature={signature?.plainText ?? null}
      sender={sender?.name ?? null}
      onAddSignature={signature ? reply.addSignature : undefined}
      links={props.links}
      id={props.id}
      frozen={props.frozen}
    />
  );
}

/** The head, the fields, the notices and the editor: the same parts at every width (LEA-514 C5). */
function Composer(props: {
  thread: InboxThread;
  name: string;
  reply: ReplyText;
  frozen: boolean;
  textarea: React.RefObject<HTMLTextAreaElement | null>;
  start: StartFromDraft | null;
  /** Why the agent's draft is not offered, said where the offer would be. */
  waits: string | null | undefined;
  held: HeldDraft | null;
  endingError: string;
  files: ReplyFiles;
  parent: InboxMessage | undefined;
  edit: ReplyRecipientEdit;
  cadence: number;
  links: ReturnType<typeof useEditorLinks>;
  mode: ReplyMode;
  onCollapse: () => void;
  sender: ReplySender | null;
  personal: PersonalComposerControl;
}) {
  const { reply, thread, frozen, start, held, links } = props;
  const ids = useId();
  const replacing = useRef(false);
  const beforeInput = useCallback((field: HTMLTextAreaElement) => {
    replacing.current =
      field.value.length > 0 &&
      field.selectionStart === 0 &&
      field.selectionEnd === field.value.length;
  }, []);
  useEffect(() => {
    if (reply.format !== "text") return;
    const field = props.textarea.current;
    if (!field) return;
    const record = () => beforeInput(field);
    field.addEventListener("beforeinput", record);
    return () => field.removeEventListener("beforeinput", record);
  }, [props.textarea, reply.format, beforeInput]);
  const editor = reply.editor;
  return (
    <div className="lcr-i2-compose">
      <ReplyHead mode={props.mode} onFold={props.onCollapse} />
      <ReplyFields
        thread={thread}
        parent={props.parent}
        edit={props.edit}
        frozen={frozen}
        sender={props.sender}
      />
      {props.cadence > 0 && (
        <div className="lcr-reply-notice" role="note">
          <p>
            {props.cadence} {props.cadence === 1 ? "message" : "messages"} from us since their last
          </p>
        </div>
      )}
      <DraftEntry start={start} />
      <PersonalComposerNotice control={props.personal} />
      {start?.asking && <ReplaceConfirm start={start} />}
      {!start && props.waits && (
        <div className="lcr-reply-notice lcr-i2-draft-waits" data-tone="info" role="note">
          <p>{props.waits}</p>
        </div>
      )}
      {reply.stale && (
        <div className="lcr-reply-notice" data-tone="warn" role="note">
          <p>Written before {props.name}'s newest message. Check it still answers it.</p>
        </div>
      )}
      {reply.warned && (
        <div className="lcr-reply-notice" data-tone="info" role="status">
          <p>Plain text drops the bold, italics, lists and link styling.</p>
        </div>
      )}
      {held?.aids.waitingOn && <WaitingLine waitingOn={held.aids.waitingOn} />}
      <EndingToolbar frozen={frozen} held={held} endingId={`${ids}-ending`} />
      {held && <HeldEnding held={held} frozen={frozen} id={`${ids}-ending`} />}
      {/* Why a change of ending did not save, even once the box no longer holds that draft. */}
      {props.endingError && (
        <div className="lcr-reply-notice" data-tone="warn" role="alert">
          <p>{props.endingError}</p>
        </div>
      )}
      {/* Above the editor, not inside its scroll: the link field opens over the words, in view. */}
      {reply.format === "html" && !frozen && <LinkLayer links={links} />}
      {reply.format === "html" ? (
        <div className="lcr-i2-reply-body">
          <EditorContent editor={editor} />
        </div>
      ) : (
        <textarea
          ref={props.textarea}
          className="lcr-i2-plain"
          aria-label="Your reply"
          placeholder={`Write your reply to ${props.name}`}
          value={reply.text}
          readOnly={frozen}
          onPaste={(event) => beforeInput(event.currentTarget)}
          onKeyDown={(event) => {
            if (
              (!event.metaKey && !event.ctrlKey && event.key.length === 1) ||
              event.key === "Backspace" ||
              event.key === "Delete"
            )
              beforeInput(event.currentTarget);
          }}
          onChange={(event) => {
            reply.write(event.target.value, replacing.current);
            replacing.current = false;
          }}
        />
      )}
      <ReplyFileList files={props.files} frozen={frozen} />
      <ReplyHints
        reply={reply}
        held={held}
        sender={props.sender}
        links={links}
        id={`${ids}-hints`}
        frozen={frozen}
      />
    </div>
  );
}

type Note = { text: string; tone: "muted" | "warn" | "done"; check?: boolean };

function unkeptReplyNote(): Note {
  return {
    text: "Not kept: this browser would not store it. Keep this reply open until you send it.",
    tone: "warn",
  };
}

/** Said when a send went and Bison did not list every file on it: look, never send again. */
const FILES_UNCONFIRMED =
  "Reply sent. Files could not be confirmed. Check Bison before taking action.";

type SendState = NonNullable<Sending["status"]>["send"];

/** What the send itself last did, when that is the news: refused, under way or gone. */
function sendNote(send: SendState | undefined): Note | null {
  if (send?.state === "refused" && send.reason === "settled_not_sent")
    return { text: "You checked Bison and it did not go out. You can send again.", tone: "muted" };
  if (send?.state === "refused")
    return {
      text: `Not sent: ${refusalText(send.reason)}. Nothing left your outbox, so you can try again.`,
      tone: "warn",
    };
  if (send?.state === "claimed") return { text: "Sending…", tone: "muted" };
  if (send?.state !== "sent") return null;
  return send.filesConfirmed === false
    ? { text: FILES_UNCONFIRMED, tone: "warn" }
    : { text: "Sent", tone: "done" };
}

/** What the box's files hold Send for: one that did not upload, else ones still uploading. */
function filesNote(files: ReplyFiles): Note | null {
  const failed = files.pending.filter((file) => file.state === "failed");
  if (failed.length > 0)
    return {
      text: failed.some((file) => file.retryable)
        ? "A file did not upload. Try it again or remove it to send."
        : "A file did not upload. Remove it to send.",
      tone: "warn",
    };
  return files.uploading
    ? { text: "Send waits until the files are uploaded", tone: "muted" }
    : null;
}

/** Whether the words are kept, and where they go from here. */
function keptNote(sending: Sending, reply: ReplyText): Note | null {
  if (!reply.empty && !reply.kept) return unkeptReplyNote();
  if (sending.status?.send?.state === "cancelled")
    return { text: "Send undone. Your text is back.", tone: "muted" };
  return null;
}

/** The one line beside the actions: what happened last, else whether the text is kept. */
function replyNote(sending: Sending, reply: ReplyText, files: ReplyFiles): Note | null {
  const send = sending.status?.send;
  // An unknown outcome has its own notice above; nothing here may read as a way to send again.
  if (sending.unknown || send?.state === "uncertain") return null;
  if (sending.error) return { text: sending.error, tone: "warn" };
  const news = sendNote(send) ?? filesNote(files);
  if (news) return news;
  if (sending.statusFailed)
    return { text: "Send status could not load, so Send waits.", tone: "warn", check: true };
  return keptNote(sending, reply);
}

function ReplyNote(props: { note: Note | null; onCheck: () => void }) {
  const { note } = props;
  return (
    <p className="lcr-i2-reply-note" data-tone={note?.tone} role="status">
      {note?.tone === "done" && <CheckIcon size={14} />}
      {note?.text}
      {note?.check && (
        <>
          {" "}
          <button type="button" className="lcr-linklike" onClick={props.onCheck}>
            Check again
          </button>
        </>
      )}
    </p>
  );
}

/** Cmd or Ctrl and Enter, on a desktop: the Send button's tip names it. */
const SEND_KEYS = "Meta+Enter Control+Enter";

/** The foot of the sheet or the tray: the hold, else the format, the note, Attach and Send. */
function ReplyFoot(props: {
  sending: Sending;
  reply: ReplyText;
  files: ReplyFiles;
  frozen: boolean;
  sendBlocked: boolean;
  name: string;
  links: ReturnType<typeof useEditorLinks>;
  showKeys: boolean;
  sender: ReplySender | null;
  phone: boolean;
  onFormat: (format: Format) => void;
  onSend: () => void;
  /** Save draft (LEA-513); offered only where the server keeps this person's drafts. */
  draft: DraftControl;
}) {
  const { sending, reply } = props;
  const send = sending.status?.send;
  if (send?.state === "queued")
    return (
      <div className="lcr-compose-foot lcr-i2-reply-foot">
        <HoldRow
          send={send}
          name={props.name}
          as={senderFirstName(props.sender)}
          busy={sending.busy}
          onUndo={() => void sending.undo()}
          grab
        />
      </div>
    );
  const empty = reply.empty;
  const unknown = sending.unknown || send?.state === "uncertain";
  return (
    <ComposerFoot
      format={
        <FormatSwitch value={reply.format} disabled={props.frozen} onChange={props.onFormat} />
      }
      tools={
        <>
          <FormatTools reply={reply} frozen={props.frozen} links={props.links} />
          {sending.status && !unknown && (
            <SignatureTool
              sender={props.sender}
              signed={reply.signed}
              onAdd={reply.addSignature}
              onRemove={reply.removeSignature}
              frozen={props.frozen}
              phone={props.phone}
            />
          )}
        </>
      }
      status={
        <>
          <ReplyNote note={replyNote(sending, reply, props.files)} onCheck={sending.check} />
          <DraftStatus draft={props.draft} />
        </>
      }
      actions={
        <>
          {sending.status && !unknown && (
            <AttachButton files={props.files} disabled={props.frozen} />
          )}
          {props.draft.available && (
            <button
              type="button"
              className="lcr-small-button lcr-ghost"
              disabled={props.frozen || props.draft.saving || props.draft.saved}
              onClick={props.draft.onSave}
            >
              Save draft
            </button>
          )}
          {sending.status && !unknown && (
            <button
              type="button"
              className="lcr-primary"
              disabled={
                props.frozen || props.sendBlocked || sending.locked || empty || !props.files.ready
              }
              {...(props.showKeys
                ? { "aria-keyshortcuts": SEND_KEYS, title: "Send (⌘ Enter)" }
                : {})}
              onClick={props.onSend}
            >
              <SendIcon size={16} />
              Send
            </button>
          )}
        </>
      }
    />
  );
}

type DraftControl = {
  readonly available: boolean;
  readonly saving: boolean;
  readonly saved: boolean;
  readonly uploading: boolean;
  readonly onSave: () => void;
};

/**
 * Saving, Saved, or still uploading (LEA-513 D1). Said only for words the server holds; a failure
 * is the composer's own notice, with its Retry. Polite, so a screen reader hears it once.
 */
function DraftStatus({ draft }: { draft: DraftControl }) {
  const said = !draft.available
    ? ""
    : draft.saving
      ? "Saving draft…"
      : draft.saved && draft.uploading
        ? "Words saved; files are still uploading"
        : draft.saved
          ? "Draft saved"
          : "";
  return (
    <span className="lcr-i2-draft-status" role="status" aria-live="polite">
      {said}
    </span>
  );
}

function replyLabel(name: string, followUp: boolean, sent: boolean): string {
  return followUp || sent ? `Follow up with ${name}` : `Reply to ${name}`;
}

/** "Reply to Noor + 1": the lead and how many of their colleagues the bottom box also writes to. */
function audienceWords(label: string, recipients: ReturnType<typeof replyAudience>["recipients"]) {
  const others = (recipients?.to.length ?? 1) - 1 + (recipients?.cc.length ?? 0);
  return others > 0 ? `${label} + ${others}` : label;
}

/** The folded bar keeps the lead as recipient, including after a confirmed send. */
function DockBar(props: {
  sending: Sending;
  reply: ReplyText;
  name: string;
  /** Whom the reply signs as, for the held send's line. */
  as: string | null;
  label: string;
  cadence: number;
  showKeys: boolean;
  bisonUrl: string | null;
  opener: React.RefObject<HTMLButtonElement | null>;
  start: StartFromDraft | null;
  waits: string | null | undefined;
  onOpen: () => void;
}) {
  const { sending, reply, name } = props;
  const send = sending.status?.send;
  const state = send?.state;
  if (sending.unknown || state === "uncertain")
    return (
      <Uncertain
        send={send}
        kind="reply"
        bisonUrl={props.bisonUrl}
        busy={sending.busy}
        onSettle={sending.settle}
        onShow={reply.empty ? null : props.onOpen}
      />
    );
  if (send && state === "queued")
    return (
      <HoldRow
        send={send}
        name={name}
        as={props.as}
        busy={sending.busy}
        onUndo={() => void sending.undo()}
        grab={false}
      />
    );
  if (send && state === "claimed")
    return (
      <p className="lcr-send-status" role="status">
        {claimedWords(send, name)}
      </p>
    );
  const gist = reply.empty ? "" : reply.text.trim().replace(/\s+/gu, " ");
  return (
    <div className="lcr-reply-closed">
      <button
        type="button"
        ref={props.opener}
        className="lcr-reply-open"
        {...(props.showKeys ? { "aria-keyshortcuts": "R" } : {})}
        onClick={props.onOpen}
      >
        <ReplyIcon size={16} />
        <span className="lcr-i2-dock-label">{props.label}</span>
        {props.cadence > 0 && (
          <span className="lcr-i2-dock-cadence">{props.cadence} from us since their last</span>
        )}
        {gist && (
          <>
            <span className="lcr-i2-dock-gist" aria-hidden="true">
              {gist}
            </span>
            <span className="lcr-i2-dock-kept">Kept until you send it</span>
          </>
        )}
        {props.showKeys && (
          // The button names its key (`aria-keyshortcuts`); the keycap is the eye's, as the head's are.
          <span className="lcr-kbd" aria-hidden="true">
            R
          </span>
        )}
      </button>
      <DraftEntry start={props.start} />
      {state === "sent" && send?.filesConfirmed === false && (
        <p className="lcr-send-status" data-tone="warn" role="status">
          {FILES_UNCONFIRMED}
        </p>
      )}
      {!props.start && props.waits && (
        <p className="lcr-i2-draft-waits" role="note">
          {props.waits}
        </p>
      )}
    </div>
  );
}

/** What the page lends the box: who is writing, and where held sends and unkept text report. */
export type ReplyWiring = {
  readonly userId: string;
  readonly onHeld: (hold: ReplyHold) => void;
  readonly onUnsaved: (unsaved: boolean) => void;
};

/**
 * Watches the send for the page: a hold or an unknown outcome is reported; a sent reply is
 * forgotten and the box goes back to its default, the lead, so a past choice never sticks.
 */
function useSendReports(
  sending: Sending,
  reply: ReplyText,
  hold: ReplyHold,
  wiring: ReplyWiring,
  reset: () => void,
  personal: PersonalComposerControl,
  /** For a message's box, the send it pressed; null for the top box, which reports every send. */
  pressed: React.RefObject<string | null> | null,
) {
  const state = sending.status?.send?.state;
  const { onHeld, onUnsaved } = wiring;
  const ownSend = (id: string | undefined) => !pressed || (!!id && id === pressed.current);
  const live =
    (state === "queued" || state === "claimed" || sending.unknown) &&
    ownSend(sending.status?.send?.id);
  const own = useRef(ownSend);
  own.current = ownSend;
  useEffect(() => {
    if (live) onHeld(hold);
  }, [live, onHeld, hold]);
  const { clear } = reply;
  const sentId = sending.status?.send?.id;
  const handled = useRef<string | null>(null);
  const { confirmed } = personal;
  useEffect(() => {
    if (state !== "sent" || !sentId || handled.current === sentId || !own.current(sentId)) return;
    handled.current = sentId;
    void confirmed(sentId).then((localOnly) => {
      if (localOnly) {
        keepUnsent(hold.unsentKey, null);
        clear();
      }
      reset();
    });
  }, [state, sentId, hold.unsentKey, clear, reset, confirmed]);
  // Text this browser would not store exists only on screen: leaving asks first.
  const unkept = !reply.kept && !reply.empty;
  useEffect(() => {
    onUnsaved(unkept);
  }, [unkept, onUnsaved]);
  useEffect(() => () => onUnsaved(false), [onUnsaved]);
}

/**
 * Once a reply with files is confirmed sent, the box's files are read again: the server empties a box
 * that still holds exactly what went, and the browser then forgets it (`use-reply-files.ts`).
 */
function useSettleFiles(sending: Sending, files: ReplyFiles) {
  const send = sending.status?.send;
  const settled = send?.state === "sent" && (send.files?.length ?? 0) > 0 ? send.id : null;
  const settle = useRef(files.settle);
  settle.current = files.settle;
  useEffect(() => {
    if (settled) void settle.current().catch(() => {});
  }, [settled]);
}

type ReplyBoxProps = {
  workspaceId: string;
  thread: InboxThread;
  /** Bumped by the R key: open the box and put the caret in it. */
  focus: number;
  phone: boolean;
  showKeys: boolean;
  wiring: ReplyWiring;
  onFormat: (format: Format) => void;
  /** The reply agent's draft, for "Edit in reply box"; absent with the agent off or no draft. */
  draft?: StartingDraft | null;
  /** How the agent's card reaches this box (`reply-box-link.ts`); absent with the agent off. */
  link?: ReplyBoxLink | null;
  /**
   * Why the agent's draft is not offered, said where its offer would be: its commitments wait on
   * the card (`draft-slots.tsx`), and its text holds what is not yet agreed.
   */
  draftWaits?: string | null;
};

/** A box's place: the reply to the lead at the thread's top, or a reply above one message. */
type BoxPlace = { readonly key: BoxKey; readonly target: string | null };
const TOP_BOX: BoxPlace = { key: TOP, target: null };
const noReset = () => {};

/**
 * The box at the top of the thread: the reply to the lead (LEA-514). A Reply on the lead's own
 * message opens it in that preset; every other message's Reply and Forward open that message's own
 * box above its card (`MessageBoxes`), the choice held by `thread-boxes.tsx`.
 */
export function ReplyBox(props: ReplyBoxProps) {
  const boxes = useBoxes();
  const sending = useReplySend(
    props.workspaceId,
    props.thread.conversation.id,
    props.thread.conversation.leadMessageId,
  );
  return (
    <ReplyComposer
      {...props}
      place={TOP_BOX}
      source={boxes?.top ?? null}
      reset={boxes?.resetTop ?? noReset}
      forwardEnabled={!!sending.status}
    />
  );
}

/**
 * The boxes directly above one message's card (LEA-514); a message a thread read left out keeps its
 * boxes at the thread's top. The agent's draft, its link and R belong to the top box alone.
 */
export function MessageBoxes(props: ReplyBoxProps & { message: InboxMessage }) {
  const boxes = useBoxes();
  const sending = useReplySend(
    props.workspaceId,
    props.thread.conversation.id,
    props.thread.conversation.leadMessageId,
  );
  if (!boxes) return null;
  return boxes
    .above(props.message.id)
    .map((box) => (
      <MessageBoxItem
        key={keyOf(box)}
        {...props}
        box={box}
        close={() => boxes.close(keyOf(box))}
        forwardEnabled={!!sending.status}
      />
    ));
}

function MessageBoxItem(
  props: ReplyBoxProps & { box: MessageBox; close: () => void; forwardEnabled: boolean },
) {
  const { box } = props;
  const planId = props.thread.conversation.id;
  const selection = box.kind === "reply" ? box.selection : 0;
  const all = box.kind === "reply" && box.all;
  // Stable while nothing is asked again: a new source opens the box and puts the caret in it.
  const source = useMemo(
    () => ({ messageId: box.message.id, all, selection }),
    [box.message.id, all, selection],
  );
  if (box.kind === "forward")
    return (
      <ShownWhenOpened>
        <ForwardBox
          phone={props.phone}
          mailbox={props.thread.mailbox}
          bisonUrl={props.thread.bisonUrl}
          workspaceId={props.workspaceId}
          planId={planId}
          userId={props.wiring.userId}
          message={box.message}
          replyModes={replyModesFor(box.message, props.thread.mailbox)}
          onReply={(all) => openReply(props.workspaceId, planId, box.message.id, all)}
          onClose={props.close}
        />
      </ShownWhenOpened>
    );
  return (
    <ReplyComposer
      {...props}
      focus={0}
      showKeys={false}
      draft={null}
      link={null}
      draftWaits={null}
      place={{ key: keyOf(box), target: box.message.id }}
      source={source}
      reset={props.close}
    />
  );
}

/** How long a just-opened box is kept in view while it fills in (its forwarded email, its fields). */
const SHOWN_WHILE_FILLING_MS = 2_000;

/**
 * A box opened above its card keeps the card where it was (`use-view-held.ts`); where that leaves the
 * box out of the view, it is brought just into it, and kept there while it grows as its parts load,
 * so what was opened is always seen. The reader's own scroll ends that.
 */
function ShownWhenOpened(props: { children: React.ReactNode }) {
  const place = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = place.current;
    if (!element) return;
    const reveal = () => element.firstElementChild?.scrollIntoView?.({ block: "nearest" });
    const frame = requestAnimationFrame(reveal);
    if (typeof ResizeObserver === "undefined") return () => cancelAnimationFrame(frame);
    const growing = new ResizeObserver(reveal);
    growing.observe(element);
    const stop = () => growing.disconnect();
    const timer = setTimeout(stop, SHOWN_WHILE_FILLING_MS);
    window.addEventListener("wheel", stop, { once: true, passive: true });
    window.addEventListener("touchstart", stop, { once: true, passive: true });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      stop();
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
    };
  }, []);
  return (
    <div className="lcr-i2-box-place" ref={place}>
      {props.children}
    </div>
  );
}

/** The reply presets a message offers: a sequence email has none (Forward only). */
function replyModesFor(message: InboxMessage, mailbox: string | null): ComposeMode[] {
  if (!/^bison:reply:[1-9]\d*$/u.test(message.id)) return [];
  return hasOthers(message, mailbox) ? ["reply", "reply-all"] : ["reply"];
}

function composerFrozen(sending: Sending, approving: boolean, preparing: boolean) {
  return (
    sending.busy ||
    sending.unknown ||
    (sending.held && sending.status?.send?.state !== "sent") ||
    approving ||
    preparing
  );
}

function DraftSendError({ error }: { error: string }) {
  return error ? <p role="alert">{error}</p> : null;
}

function draftPreparation(loaded: number | null, draft: StartingDraft | null | undefined) {
  if (loaded === null) return undefined;
  if (draft?.prepareSend) return draft.prepareSend;
  return async () => {
    throw new Error(
      "This draft is no longer available. Load the latest draft or write your own reply.",
    );
  };
}

function draftSendBlocked(loaded: number | null, draft: StartingDraft | null | undefined) {
  return loaded !== null && (draft?.held === true || draft?.sendBlocked === true);
}

/** The mode the box is in, the others its message offers, and how choosing one moves it. */
function useReplyMode(
  props: ReplyBoxProps & { forwardEnabled: boolean },
  source: TopSource,
  parent: InboxMessage | undefined,
  all: boolean,
  cc: number,
): ReplyMode {
  const plan = props.thread.conversation.id;
  const offers = parent ? replyModesFor(parent, props.thread.mailbox) : [];
  const mode: ComposeMode = all && cc > 0 ? "reply-all" : "reply";
  const followUp = !source && props.thread.conversation.lastFrom === "seller";
  return {
    mode,
    word: followUp ? "Follow up" : undefined,
    modes: [...offers, ...(props.forwardEnabled && parent ? (["forward"] as const) : [])],
    onMode: (next) => {
      if (!parent || next === mode) return;
      if (next === "forward") openForward(props.workspaceId, plan, parent.id);
      else openReply(props.workspaceId, plan, parent.id, next === "reply-all");
    },
    source: parent ? sourceWords(parent) : null,
  };
}

/**
 * Whether the box is open and where its caret goes: a message's Reply, the agent's request and R
 * each open it with the caret in the words; the fold puts the focus back on the bar.
 */
function useBoxOpening(
  focus: number,
  source: TopSource,
  reply: ReplyText,
  textarea: React.RefObject<HTMLTextAreaElement | null>,
) {
  const [open, setOpen] = useState(false);
  const [caret, setCaret] = useState(0);
  const opener = useRef<HTMLButtonElement>(null);
  const dock = useRef<HTMLElement>(null);
  // A fold that takes the focus with it lands it on the box once the sheet is gone.
  const landing = useRef(false);
  useEffect(() => {
    if (open || !landing.current) return;
    landing.current = false;
    dock.current?.focus({ preventScroll: true });
  }, [open]);
  const reveal = (withCaret: boolean) => {
    setOpen(true);
    if (withCaret) setCaret((n) => n + 1);
  };
  useEffect(() => {
    // A box drawn for a saved draft (`selection` 0) waits folded until it is opened.
    if (!source || source.selection === 0) return;
    setOpen(true);
    setCaret((n) => n + 1);
  }, [source]);
  // R from the page: only presses after this box was drawn, not the count it was drawn with.
  const seen = useRef(focus);
  useEffect(() => {
    if (focus === seen.current) return;
    seen.current = focus;
    setOpen(true);
    setCaret((n) => n + 1);
  }, [focus]);
  // Read when the caret goes in, not as a cause: typing the first word must not move it again.
  const empty = useRef(reply.empty);
  empty.current = reply.empty;
  useEffect(() => {
    if (caret === 0) return;
    // A box holding only the signature takes the words above it.
    const top = empty.current;
    if (reply.format === "html") reply.editor?.commands.focus(top ? "start" : "end");
    else {
      textarea.current?.focus();
      if (top) textarea.current?.setSelectionRange(0, 0);
    }
  }, [caret, reply.format, reply.editor, textarea]);
  return {
    open,
    opener,
    dock,
    reveal,
    expand: () => reveal(true),
    close: () => setOpen(false),
    collapse: () => {
      setOpen(false);
      requestAnimationFrame(() => opener.current?.focus());
    },
    /**
     * The hold is over: the sheet folds to the bar. A focus it held, as the hold's row holds it after
     * Send, goes to the box, which stays through "Sending" and "Sent", not the page.
     */
    fold: () => {
      if (!open) return;
      const held = document.activeElement;
      landing.current = !held || held === document.body || !!dock.current?.contains(held);
      setOpen(false);
    },
  };
}

/**
 * Pressed here and past its hold, the reply folds the box: it now stands in the thread as it goes
 * (`outgoing-reply.ts`). A send that was already under way when the box was drawn leaves it as it
 * is. Refused after the fold, the box opens again on the words, beside why they were not sent.
 * Decided in the render the status moves in, not after it: the thread's card and the folded bar
 * are then laid out together, so the card comes into view above the bar that takes room under it.
 */
function useFoldAfterHold(
  send: SendState | undefined,
  pressed: React.RefObject<string | null>,
  fold: () => void,
  reopen: () => void,
) {
  const id = send?.id;
  const state = send?.state;
  const [seen, setSeen] = useState({ id, state });
  const [folded, setFolded] = useState<string | null>(null);
  if (seen.id === id && seen.state === state) return;
  setSeen({ id, state });
  if (!id || id !== pressed.current) return;
  if ((state === "claimed" || state === "sent") && folded !== id) {
    setFolded(id);
    fold();
  } else if (state === "refused" && folded === id) {
    setFolded(null);
    reopen();
  }
}

/** Send, and Cmd or Ctrl with Enter for it: one deliberate send of the words as they stand. */
function useSendReply(input: {
  ready: boolean;
  parent: InboxMessage | undefined;
  reply: ReplyText;
  files: ReplyFiles;
  audience: { seenThrough: string; all: boolean; recipients: ForwardRecipients | null };
  sending: Sending;
  draftSend: ReturnType<typeof useDraftSend>;
  draft: StartingDraft | null | undefined;
  outgoing: ReturnType<typeof useOutgoingRecorder>;
  selected: boolean;
  personal: PersonalComposerControl;
  /** A message's box: its saved draft is bound to the send as that box's (LEA-514). */
  target: string | null;
}) {
  const { sending, outgoing, selected } = input;
  // What goes is kept for the thread as it goes, and forgotten if this press queued nothing.
  const dispatch = async (request: InboxSendRequest | InboxDraftSendRequest) => {
    const composerRevision = await input.personal.prepare(request.operationId, {
      format: request.format,
      plainText: request.plainText,
      html: request.html,
      fromDraft: input.reply.fromDraft,
    });
    const bound =
      composerRevision === undefined
        ? request
        : {
            ...request,
            composerRevision,
            ...(input.target ? { composerTarget: input.target } : {}),
          };
    outgoing.record(bound, selected);
    if (input.target) boxSends.add(bound.operationId);
    await sending.send(bound);
    outgoing.settle(bound);
  };
  const send = () => {
    const { parent, reply, files } = input;
    const { seenThrough, all, recipients } = input.audience;
    if (!input.ready || !parent || !recipients || reply.empty) return;
    const attached = files.reference();
    void input.draftSend.send(
      {
        operationId: crypto.randomUUID(),
        messageId: parent.id,
        reply: { seenThrough, all, recipients },
        html: reply.html(),
        plainText: reply.text,
        simulation: false,
        format: reply.format,
        ...(attached ? { files: attached } : {}),
      },
      dispatch,
      draftPreparation(reply.fromDraft, input.draft),
    );
  };
  const sendKey: React.KeyboardEventHandler = (event) => {
    if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey) || event.nativeEvent.isComposing)
      return;
    event.preventDefault();
    send();
  };
  return { send, sendKey };
}

/** The open sheet's name: its mode and the message it answers, "Reply all, priya@… · 17 Oct". */
function sheetWords(mode: ReplyMode): string {
  const word = mode.word ?? (mode.mode === "reply-all" ? "Reply all" : "Reply");
  return mode.source ? `${word}, ${mode.source}` : word;
}

/** A change of format: the box's words follow it, and the conversation keeps it for next time. */
function formatChooser(reply: ReplyText, frozen: boolean, keep: (format: Format) => void) {
  return (next: Format) => {
    if (frozen || next === reply.format) return;
    reply.choose(next);
    keep(next);
  };
}

/**
 * The box: its bar, or the open sheet in its place. The same at every width: inline at the top of
 * the conversation, with no tray over the thread on a phone (LEA-514, Dawid's C5, 2026-11-04).
 */
function ReplyDock(props: {
  label: string;
  sheetLabel: string;
  source: string | undefined;
  opening: ReturnType<typeof useBoxOpening>;
  drop: ReturnType<typeof useFileDrop>;
  sendKey: React.KeyboardEventHandler;
  bar: React.ReactNode;
  uncertain: React.ReactNode;
  composer: React.ReactNode;
  error: React.ReactNode;
  foot: React.ReactNode;
}) {
  const { opening, drop } = props;
  return (
    <section
      className="lcr-bar lcr-i2-dock"
      data-open={opening.open || undefined}
      aria-label={props.label}
      data-reply-source={props.source}
      onKeyDownCapture={props.sendKey}
      ref={opening.dock}
      tabIndex={-1}
    >
      {opening.open ? (
        <fieldset
          className="lcr-sheet lcr-i2-reply-sheet"
          aria-label={props.sheetLabel}
          data-dropping={drop.dropping}
          {...drop.handlers}
          onKeyDown={(event) => {
            // The editor's own keymap already claims Escape (it selects the parent node), so its
            // default is prevented by now; only the link field, which stops it, keeps it.
            if (event.key !== "Escape") return;
            event.preventDefault();
            event.stopPropagation();
            opening.collapse();
          }}
        >
          {props.uncertain}
          {props.composer}
          {props.error}
          {props.foot}
        </fieldset>
      ) : (
        props.bar
      )}
    </section>
  );
}

/** Sends a message's own box pressed (LEA-514): the top box leaves them to that box to draw. */
const boxSends = new Set<string>();

/**
 * The send as this box draws it. The server reports the plan's one send in flight to every box, and
 * every box stays frozen by it (`composerFrozen`, `locked`); but its hold, Undo, state and an unknown
 * outcome are drawn once: a message box draws the send it pressed; the top box every other reply,
 * and a forward unless a forward box is open to draw it, so one from an earlier visit is never lost.
 */
function sendShownHere(
  sending: Sending,
  place: { top: boolean; forwarding: boolean },
  pressed: string | null,
): Sending {
  const send = sending.status?.send;
  if (!send) return sending;
  const topDraws = send.forwardTo ? !place.forwarding : !boxSends.has(send.id);
  const here = place.top ? topDraws : !send.forwardTo && send.id === pressed;
  if (here || !sending.status) return sending;
  return { ...sending, unknown: false, status: { ...sending.status, send: null } };
}

/**
 * What a box's place decides (LEA-514): the lead's box keeps the address every earlier tab used and
 * the lead's draft; a message's box has its own key and draft, and reports only the sends it pressed.
 */
function placeParts(place: BoxPlace, lead: string, to: InboxThread["conversation"]) {
  const { target } = place;
  if (!target)
    return {
      top: true,
      key: lead,
      sourceMessageId: to.leadMessageId,
      draft: LEAD_REPLY,
      reports: (_outgoing: { pressed: React.RefObject<string | null> }) => null,
    };
  return {
    top: false,
    key: `${lead}:reply:${encodeURIComponent(target)}`,
    sourceMessageId: target,
    draft: { target, kind: "reply" as const },
    reports: (outgoing: { pressed: React.RefObject<string | null> }) => outgoing.pressed,
  };
}

/** The bar's words: a message's box names the message it answers, the lead's names the lead. */
function boxLabel(
  top: boolean,
  parent: InboxMessage | undefined,
  name: string,
  followUp: boolean,
  send: SendState | undefined,
): string {
  if (!top && parent) return `Reply to ${sourceWords(parent)}`;
  return replyLabel(name, followUp, send?.state === "sent");
}

/**
 * One box open at a time (LEA-514): opening this one, by its bar or R, folds every other; another
 * opening folds this one, its words kept. A message's box folded with no words goes.
 */
function useOneOpen(
  place: BoxPlace,
  opening: ReturnType<typeof useBoxOpening>,
  empty: boolean,
  close: () => void,
) {
  const boxes = useBoxes();
  const { open } = opening;
  // Said once as the box opens: the controller's functions are new with every change it makes.
  const opened = useRef(boxes?.opened);
  opened.current = boxes?.opened;
  useEffect(() => {
    if (open) opened.current?.(place.key);
  }, [open, place.key]);
  const current = boxes?.open ?? null;
  const fold = useRef(opening.close);
  fold.current = opening.close;
  const isOpen = useRef(open);
  isOpen.current = open;
  useEffect(() => {
    if (current !== null && current !== place.key && isOpen.current) fold.current();
  }, [current, place.key]);
  const wasOpen = useRef(false);
  const closing = useRef(close);
  closing.current = close;
  useEffect(() => {
    if (open) wasOpen.current = true;
    else if (place.target && wasOpen.current && empty) closing.current();
  }, [open, empty, place.target]);
}

function ReplyComposer(
  props: ReplyBoxProps & {
    place: BoxPlace;
    source: TopSource;
    reset: () => void;
    forwardEnabled: boolean;
  },
) {
  const { thread, wiring } = props;
  const to = thread.conversation;
  const name = firstName(thread);
  const { parent, all, recipients, cadence, messageId, selected, seenThrough } = replyAudience(
    thread,
    props.source,
  );
  const placed = placeParts(props.place, unsentKey(wiring.userId, props.workspaceId, to.id), to);
  const { key } = placed;
  const sending = useReplySend(props.workspaceId, to.id, messageId, "reply", null, selected);
  const edit = useReplyRecipients(thread, { parent, all, recipients }, sending.status?.send);
  const approving = useBoxApproving(props.link ?? null, to.id);
  const draftSend = useDraftSend();
  const frozen = composerFrozen(sending, approving, draftSend.busy);
  const sender = useReplySender(props.workspaceId, thread.mailbox);
  const editing = useRef<(words: ComposerWords) => ComposerLocalBase | undefined>(() => undefined);
  const reply = useReplyText(key, thread, frozen, sender?.signature ?? null, (words) =>
    editing.current(words),
  );
  // Filled once the file box exists below; the closures here run only after this render.
  const whole = useRef<(words: ComposerWords) => ComposerWords>((words) => words);
  const personal = usePersonalComposer({
    workspaceId: props.workspaceId,
    planId: to.id,
    unsentKey: key,
    sourceMessageId: placed.sourceMessageId,
    draft: placed.draft,
    held: frozen,
    hydrate: (composer, saved) => restoreWhole({ reply, edit, files }, composer, saved),
    snapshot: () => whole.current(currentWords(reply)),
  });
  editing.current = (words) => personal.edit(whole.current(words));
  const links = useEditorLinks(frozen ? null : reply.editor);
  const sendBlocked =
    personal.blocked || !edit.recipients || draftSendBlocked(reply.fromDraft, props.draft);
  const files = useReplyFiles({
    workspaceId: props.workspaceId,
    planId: to.id,
    messageId,
    unsentKey: key,
    enabled: !!sending.status,
  });
  const drop = useFileDrop(files, frozen);
  useWholeReply(whole, { all, edit, files, sender, frozen, personal, reply });
  const audienceName = heldAudience(edit.recipients, to.leadAddress, name);
  const hold = useMemo<ReplyHold>(
    () => ({ planId: to.id, messageId, name: audienceName, unsentKey: key }),
    [to.id, messageId, audienceName, key],
  );
  const outgoing = useOutgoingRecorder(props.workspaceId, thread);
  const drawing = { top: placed.top, forwarding: useBoxes()?.forwarding ?? false };
  useSettleFiles(sendShownHere(sending, drawing, outgoing.pressed.current), files);
  // A message's box reports only what it sent: the plan's one send in flight locks every box, but its
  // hold and its confirmation belong to the box that pressed Send.
  useSendReports(sending, reply, hold, wiring, props.reset, personal, placed.reports(outgoing));
  const mode = useReplyMode(props, props.source, parent, all, recipients?.cc.length ?? 0);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const opening = useBoxOpening(props.focus, props.source, reply, textarea);
  useOpenOnRequest(opening.expand, placed.top);
  useOneOpen(props.place, opening, reply.empty, props.reset);
  const start = useStartFromDraft(
    reply,
    props.draft ?? null,
    frozen,
    opening.reveal,
    draftFiles(files),
  );
  useLinked(props.link ?? null, reply, start, opening.open, to.id, key, files);
  const ending = useEndingChoice(props.draft?.aids ?? null, frozen, reply.fromDraft);
  const held = heldDraft(reply, props.draft ?? null, opening.open, ending);
  const { send, sendKey } = useSendReply({
    ready: !frozen && !sendBlocked && !sending.locked && files.ready,
    parent,
    reply,
    files,
    audience: { seenThrough, all, recipients: edit.recipients },
    sending,
    draftSend,
    draft: props.draft,
    outgoing,
    selected,
    personal,
    target: props.place.target,
  });
  useFoldAfterHold(
    sending.status?.send,
    outgoing.pressed,
    () => opening.fold(),
    () => opening.reveal(true),
  );
  const label = boxLabel(placed.top, parent, name, to.lastFrom === "seller", sending.status?.send);
  // The plan's one send in flight freezes every box; only the box it is from draws it (LEA-514).
  const shown = sendShownHere(sending, drawing, outgoing.pressed.current);
  const unknown = shown.unknown || shown.status?.send?.state === "uncertain";
  return (
    <ReplyDock
      label={label}
      sheetLabel={sheetWords(mode)}
      source={props.source?.messageId}
      opening={opening}
      drop={drop}
      sendKey={sendKey}
      bar={
        <DockBar
          sending={shown}
          reply={reply}
          name={audienceName}
          as={senderFirstName(sender)}
          label={audienceWords(label, edit.audience)}
          cadence={cadence}
          showKeys={props.showKeys}
          bisonUrl={thread.bisonUrl}
          opener={opening.opener}
          start={start}
          waits={props.draftWaits}
          onOpen={opening.expand}
        />
      }
      uncertain={
        unknown ? (
          <Uncertain
            send={shown.status?.send}
            kind="reply"
            bisonUrl={thread.bisonUrl}
            busy={sending.busy}
            onSettle={sending.settle}
          />
        ) : null
      }
      composer={
        <Composer
          thread={thread}
          name={name}
          reply={reply}
          frozen={frozen}
          textarea={textarea}
          start={start}
          waits={props.draftWaits}
          held={held}
          endingError={ending.error}
          files={files}
          parent={parent}
          edit={edit}
          cadence={cadence}
          links={links}
          mode={mode}
          onCollapse={opening.collapse}
          sender={sender}
          personal={personal}
        />
      }
      error={<DraftSendError error={draftSend.error} />}
      foot={
        <ReplyFoot
          sending={shown}
          reply={reply}
          files={files}
          frozen={frozen}
          sendBlocked={sendBlocked}
          name={audienceName}
          links={links}
          showKeys={props.showKeys}
          sender={sender}
          phone={props.phone}
          onFormat={formatChooser(
            reply,
            frozen,
            personal.state.mode === "disabled" ? props.onFormat : () => {},
          )}
          onSend={send}
          draft={draftControl(personal, unknown, files)}
        />
      }
    />
  );
}

/** A saved draft put back whole: its words, its recipient lines and its file box (LEA-513). */
function restoreWhole(
  box: { reply: ReplyText; edit: ReplyRecipientEdit; files: ReplyFiles },
  composer: PersonalComposer,
  saved: ComposerReply | null | undefined,
) {
  box.reply.hydrate(composer);
  if (!saved) return;
  box.edit.restore(savedRecipientText(saved));
  if (saved.files) box.files.restore(saved.files);
}

/**
 * Every save carries the whole reply: whether the words are the rep's own beyond the signature, the
 * recipient lines, the preset and the file box. A change to any of them is an edit.
 */
function useWholeReply(
  whole: { current: (words: ComposerWords) => ComposerWords },
  input: {
    all: boolean;
    edit: ReplyRecipientEdit;
    files: ReplyFiles;
    sender: ReplySender | null;
    frozen: boolean;
    personal: PersonalComposerControl;
    reply: ReplyText;
  },
) {
  const { files, edit } = input;
  const signature = input.sender?.signature?.plainText ?? null;
  const boxed = boxReply({
    all: input.all,
    edited: edit.edited,
    text: edit.text,
    box: files.kept || files.files.length > 0 ? files.composerId : null,
  });
  whole.current = (words) => ({
    ...words,
    authored: ownWords(words.plainText, signature).trim() !== "",
    reply: boxed,
  });
  useReplyEdits(boxed, input.frozen, () =>
    input.personal.edit(whole.current(currentWords(input.reply))),
  );
}

function draftControl(
  personal: PersonalComposerControl,
  unknown: boolean,
  files: ReplyFiles,
): DraftControl {
  return {
    available: personal.state.mode === "enabled" && !unknown,
    saving: personal.state.saving,
    saved: personal.saved,
    uploading: files.uploading,
    onSave: () => void personal.save(),
  };
}

/** The box's words as the editor holds them now. */
function currentWords(reply: ReplyText): ComposerWords {
  return {
    format: reply.format,
    plainText: reply.text,
    html: reply.html(),
    fromDraft: reply.fromDraft,
  };
}

/**
 * A change of recipients, preset or file box is an edit of the draft as much as a change of words
 * (LEA-513, "Whole reply"); the session decides whether it differs from what is saved.
 */
function useReplyEdits(boxed: ComposerReply, frozen: boolean, edit: () => void) {
  const key = JSON.stringify(boxed);
  const last = useRef(key);
  const latest = useRef(edit);
  latest.current = edit;
  useEffect(() => {
    if (last.current === key) return;
    last.current = key;
    if (!frozen) latest.current();
  }, [key, frozen]);
}
